
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Send, X } from "lucide-react";
import { getAIResponse } from "@/services/openai";

export function AIChatbot({ examValues, analysisResults, alerts, isOpen, onOpenChange }) {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: `👋 Olá! Eu sou a AXIS, sua assistente especializada em análise hormonal.

${examValues ? '📊 Já analisei seus resultados e posso ajudar com:' : '🔍 Posso ajudar você a:'}
• Interpretar seus resultados
• Explicar correlações hormonais
• Sugerir exames complementares
• Identificar padrões importantes
• Responder suas dúvidas sobre hormônios

Como posso ajudar hoje?`
      }]);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = { role: "user", content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await getAIResponse({
        examValues,
        analysisResults,
        alerts,
        userQuery: input
      });

      if (response.status === "success") {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: response.content
        }]);
      } else {
        throw new Error("Falha na resposta da IA");
      }
    } catch (error) {
      console.error("Erro no chat:", error);
      toast({
        title: "Erro na consulta",
        description: "Não foi possível obter resposta da IA. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          className="fixed bottom-24 right-6 w-96 bg-[#1a1a2e] rounded-lg shadow-lg border border-[#ff3366]/20 z-50"
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#ff3366]">AXIS</h3>
              <Button
                variant="ghost"
                className="text-[#ff3366] hover:text-[#ff3366]/80"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="h-96 overflow-y-auto mb-4 space-y-4 p-2">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg ${
                    msg.role === "user"
                      ? "bg-[#ff3366]/10 ml-8"
                      : "bg-[#16213e] mr-8"
                  }`}
                >
                  <pre className="whitespace-pre-wrap font-sans text-sm text-white/90">
                    {msg.content}
                  </pre>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="flex-1 bg-[#16213e] border-[#ff3366]/20 text-white"
                disabled={isLoading}
              />
              <Button 
                type="submit"
                className="bg-[#ff3366] hover:bg-[#ff3366]/80"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
